import { FormRestructureEntry } from "./FormRestructureEntry";
import { isFieldSetElement, isHtmlElement } from "$lib/core";

/**
 * @class FormRestructureEvent
 * @extends Event
 * @classdesc An event dispatched by HTMLFormElement nodes when their internal structure changes or when it initially loads.
 * @prop {HTMLFormElement} form The form element this event targets.
 * @prop {boolean} [init=false] True if this list was generated on page load rather than a subsequent update.
 * @prop {FormRestructureEntry[]} [removed=[]] The list of elements removed from the form.
 * @prop {FormRestructureEntry[]} [added=[]] The list of elements added to the form.
 * @prop {boolean} isEmpty Getter that returns true if there are no entries to evaluate.
 */
export class FormRestructureEvent extends Event {
  /**
   * @param {HTMLFormElement} form The form element to target.
   * @param {boolean} init True if this list was generated on page load rather than a subsequent update.
   * @param {FormRestructureEntry[]|null} [removed=[]] The list of elements removed from the form.
   * @param {FormRestructureEntry[]|null} [added=[]] The list of elements added to the form.
   * @param {EventInit} [eventInit={}] - Optional event initialization object.
   */
  constructor(form, init = false, removed = null, added = null, eventInit = {}) {
    super("restructure", eventInit);
    this.form = form;
    this.init = init;
    this.removed = removed ?? [];
    this.added = added ?? [];
  }

  /**
   * Populates the `store` using HTMLElements provided by `source`.
   * 
   * @param {Element|import("./public").RestructureDataSource} source A form or sequence of observed DOM mutations from which to aggregate relevant form entities (items, fieldsets, pages).
   * @returns {void}
   */
  populate(source) {
    if (source instanceof HTMLFormElement) {
      [...source.querySelectorAll(":scope .field")].filter(isHtmlElement).forEach(e => this.added.push(new FormRestructureEntry(e)));
      [...source.querySelectorAll(":scope fieldset")].filter(isFieldSetElement).forEach(e => this.added.push(new FormRestructureEntry(e)));
      [...source.querySelectorAll(":scope [data-webform-key]")].filter(isHtmlElement).forEach(e => this.added.push(new FormRestructureEntry(e)));
      [...source.querySelectorAll(":scope > :is(input,textarea,select)")].filter(isHtmlElement).forEach(e => this.added.push(new FormRestructureEntry(e)));
    } else if (Array.isArray(source)) {
      for (const entry of source) {
        for (const node of entry.removedNodes) {
          if (node instanceof HTMLElement) {
            const fre = new FormRestructureEntry(node);
            if (!fre.isEmpty) {
              this.removed.push(fre);
            }
          }
        }
        for (const node of entry.addedNodes) {
          if (node instanceof HTMLElement) {
            const fre = new FormRestructureEntry(node);
            if (!fre.isEmpty) {
              this.added.push(fre);
            }
          }
        }
      }
    }
  }

  get isEmpty() {
    return this.removed.length == 0
      && this.added.length == 0;
  }
}
